import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, FormHelperText, Grid, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { renderClose, useDialog } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { getMostImportantValidationMessage, propBoolField, propEnumField, propNumberField, propSimpleEnumField, propTextField } from "../../common/entityStoreForm";
import { propCurrency, propLengthMax, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { InventoryArticleDto, SaleArticleDetailDto, SaleArticleDetailProductDto, SaleArticleDto } from "./types";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { UseListStore, createListStore } from "../../common/listStore";
import { distinctUntilChanged, map } from "rxjs";
import { EnumItemDto, MailProductCode, packagesTypes } from "../../common/enum";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { Edit, Delete, Add } from "@mui/icons-material";
import { SaleArticleDetailPackageItemDialog } from "./SaleArticleDetailPackageItemDialog";
import shallow from "zustand/shallow";
import { fullJoin, newId, useRxJsSub } from "../../common/utils";
import { keyBy, round, sum } from "lodash";

export const SaleArticleDetailDialog = (props: {
    dialogProps: DialogProps;
    close: () => void;
    saleArticle: SaleArticleDto;
    saleArticleDetail: SaleArticleDetailDto | null;
    onChange(): void;
    useProductsStore: UseListStore<SaleArticleDetailProductDto>;
    useSaleArticleDetailsStore: UseListStore<SaleArticleDetailDto>;
    useInventoryArticlesStore: UseListStore<InventoryArticleDto>;
    inventoryArticle: InventoryArticleDto;
}) => {
    const [useStore] = useState(() => {
        return createEntityStore<SaleArticleDetailEditDto>({
            data: props.saleArticleDetail ?? {
                saleArticleId: props.saleArticle.id,
                price: null,
                useDiscount: false,
                categories: "",
                articleName: "",
                isOnlyForAuto: false,
                packageType: null,
                packageItems: getPackageItemsFromTemplate(props.inventoryArticle, null, props.useInventoryArticlesStore.getState().items),
            },
            path: "articles/saleArticleDetails",
            id: props.saleArticleDetail != null ? props.saleArticleDetail.id : null,
            getNewId: p => p.id!,
            onSaved() {
                props.onChange();
                props.close();
            },
            onFetched() {
                useStore.getState().setServerDataProp("packageItemsError", undefined);
            },
            propValidators: [
                propValidValue("price", "priceFormatted"),
                propRequired("price"),
                propCurrency("price"),
                propMinValue("price", 0),

                propRequired("projectId"),
                propValueValidator("projectId", (p, e) => props.useSaleArticleDetailsStore.getState().items
                    .some(i => i.projectId === p && i.id !== e.id) && "Sales article detail with same project already exists."),

                propLengthMax("categories", 200),
                propLengthMax("articleName", 100),
                propValueValidator("packageItems", (_p, e) => e.packageItemsError),
            ]
        })
    });

    const [useSaleArticlesStore] = useState(() => createListStore<EnumItemDto & { baseQuantity: number; }>({
        data: props.useInventoryArticlesStore.getState().items$.pipe(map(p => ({
            items: p.items.flatMap(i => i.saleArticles.map(s => ({ id: s.id, code: s.code, name: s.code + " " + s.name, baseQuantity: s.baseQuantity }))),
            isFetching: p.isFetching,
        })))
    }));
    const [useSaleArticlesEnumStore] = useState(() => createListStore<EnumItemDto>({ data: useSaleArticlesStore.getState().items$ }));

    const product = props.useProductsStore(p => p.items.find(p => p.code === MailProductCode));
    const [useProjectsStore] = useState(() => createListStore<EnumItemDto>({
        data: props.useProductsStore.getState().items$.pipe(map(products => ({
            items: (products.items.find(p => p.code === MailProductCode)?.projects || [])
                .map(p => ({ id: p.id, name: p.codeAndName })),
            isFetching: products.isFetching
        }))),
    }));

    const { isNew, packageItemsValidationMessage, areItemsMatchingTemplate } = useStore(p => ({
        isNew: props.saleArticleDetail == null && p.clientData.id == null,
        packageItemsValidationMessage: getMostImportantValidationMessage(p, "packageItems"),
        areItemsMatchingTemplate: getAreItemsMatchingTemplate(props.inventoryArticle, p.clientData.packageItems, props.useInventoryArticlesStore.getState().items),
    }));

    const [usePackageItemsStore] = useState(() => createListStore<SaleArticleDetailPackageItemEditDto>({
        data: useStore.getState().clientData$.pipe(map(p => p.data.packageItems), distinctUntilChanged(), map(items => ({
            isFetching: false,
            items,
        }))),
        listProps: [
            { column: "quantity", label: "Quantity", width: 75, type: "number" },
            { column: "price", label: "Price", width: 75, type: "currency" },
            { column: "saleArticleId", label: "Article", flex: 1, enumStore: useSaleArticlesEnumStore },
        ],
        itemsPerPageMin: 1,
    }));

    useRxJsSub(() => useStore.getState().clientData$.pipe(map(p => ({ price: p.data.price, items: p.data.packageItems })), distinctUntilChanged(shallow))
        .subscribe(({ items, price }) => {
            useStore.getState().setClientDataProp("packageItemsError",
                items.some(p => p.saleArticleId == null) ? "All package items must have sales article assigned." :
                    items.some(p => p.quantity == null || p.price == null) ? "All package items must have price." :
                        items.some(p => p.quantity == null || p.quantity < 1) ? "All package items must have quantity." :
                            price != null && items.length > 0 && round(sum(items.map(p => p.quantity! * p.price!)), 2) !== price ? `Package items price sum does not match Project price.` :
                                    !getAreItemsMatchingTemplate(props.inventoryArticle, items, props.useInventoryArticlesStore.getState().items) ? "Package items do not match the template." :
                                undefined);
        }));

    const { selectedPackageItem, hasPackageItems } = usePackageItemsStore(p => ({
        selectedPackageItem: p.selectedItem,
        hasPackageItems: p.items.length > 0,
    }));

    const addPackageItemDialog = useDialog((close, dialogProps) => <SaleArticleDetailPackageItemDialog close={close} dialogProps={dialogProps}
        useStore={useStore} packageItem={null} useInventoryArticlesStore={props.useInventoryArticlesStore}
        inventoryArticle={props.inventoryArticle} />, undefined, {
        usingCustomDialogTag: true,
    });
    const editPackageItemDialog = useDialog((close, dialogProps) => <SaleArticleDetailPackageItemDialog close={close} dialogProps={dialogProps}
        useStore={useStore} packageItem={selectedPackageItem!} useInventoryArticlesStore={props.useInventoryArticlesStore}
        inventoryArticle={props.inventoryArticle} />, undefined, {
        usingCustomDialogTag: true,
    });

    function resetPackageItemsToTemplate() {
        const packageItems = getPackageItemsFromTemplate(props.inventoryArticle, useStore.getState().clientData.price, props.useInventoryArticlesStore.getState().items);
        useStore.getState().setClientDataProp("packageItems", packageItems);
    }

    const showPackageItems = hasPackageItems || (props.inventoryArticle.isPackage && props.inventoryArticle.showPackageContent && props.inventoryArticle.showOnInvoice);

    return <Dialog {...props.dialogProps} maxWidth={showPackageItems ? "md" : "xs"}><Box m={2}>
        {addPackageItemDialog.render()}
        {editPackageItemDialog.render()}
        <DialogTitle>{props.saleArticleDetail?.id == null ? "New sales article detail " : `Edit sales article detail`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="flex-start">
                <Grid item container xs={showPackageItems ? 5 : 12}>
                    <Grid item xs={12}><TextField value={props.saleArticle.code} label="Sales article code" disabled /></Grid>
                    <Grid item xs={12}><TextField value={props.saleArticle.name} label="Sales article description" disabled /></Grid>
                    <Grid item xs={12}><TextField value={product?.codeAndName || ""} label="Product" disabled /></Grid>
                    <Grid item xs={12}>{propEnumField(useStore)("projectId", "Project", useProjectsStore, { disabled: !isNew })}</Grid>
                    <Grid item xs={12}>{propNumberField(useStore)("price", "priceFormatted", "Project price", { isCurrency: true })}</Grid>
                    <Grid item xs={12}>{propBoolField(useStore)("useDiscount", "Use qty discount?")}</Grid>
                    <Grid item xs={12}>{propSimpleEnumField(useStore)("packageType", "Package type", packagesTypes, { addNone: true })}</Grid>
                    <Grid item xs={12}>{propTextField(useStore)("categories", "Categories")}</Grid>
                    <Grid item xs={12}>{propTextField(useStore)("articleName", "Article name")}</Grid>
                    <Grid item xs={12}>{propBoolField(useStore)("isOnlyForAuto", "Is only for auto?")}</Grid>
                </Grid>
                {showPackageItems && <Grid item xs={7} container rowSpacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6" mt={2}>Package items</Typography>
                        {renderListStore(usePackageItemsStore, {
                            onDoubleClick: item => {
                                editPackageItemDialog.show();
                            },
                            header: listHeaderItems([
                                ["edit", () => <Button color="edit" disabled={selectedPackageItem == null} title="Edit" onClick={() => {
                                    editPackageItemDialog.show();
                                }}><Edit /></Button>],
                                ["add", () => <Button color="add" title="Add" onClick={() => {
                                    addPackageItemDialog.show();
                                }}><Add /></Button>],
                                ["remove", () => <Button color="delete" disabled={selectedPackageItem == null} title="Delete" onClick={() => {
                                    useStore.getState().setClientDataProp("packageItems", useStore.getState().clientData.packageItems.filter(p => p.saleArticleId !== selectedPackageItem!.saleArticleId));
                                }}><Delete /></Button>],
                            ]),
                            getRowClass: p => p.saleArticleId == null || p.quantity == null || p.price == null ? ["redRibbon"] : [],
                        })}
                        {packageItemsValidationMessage.firstMessageType === "error" && <FormHelperText error>{packageItemsValidationMessage.firstMessage}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        {!areItemsMatchingTemplate && <Alert severity="warning">Package items do not match the template:
                            {getPackageItemsFromTemplate(props.inventoryArticle, null, props.useInventoryArticlesStore.getState().items).map(item => {
                                const ia = props.useInventoryArticlesStore.getState().items.find(i => i.id === item.inventoryArticleId);
                                return <div key={item.id}>{item.quantity}x {ia?.code ?? "???"} {ia?.name ?? "???"}</div>;
                            })}
                            <Button color="primary" sx={{ mt: 1 }} onClick={resetPackageItemsToTemplate}>
                                Reset package items to match template
                            </Button>
                        </Alert>}
                        {props.saleArticle.baseQuantity !== 1 && <Alert severity="info" sx={{ mt: 2 }}>This sales article has base quantity {props.saleArticle.baseQuantity}.</Alert>}
                    </Grid>
                </Grid>}
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </Box></Dialog >;
};

export interface SaleArticleDetailEditDto {
    id?: number;
    saleArticleId: number;
    projectId?: number;
    price: number | null;
    priceFormatted?: string;
    useDiscount: boolean;
    categories: string;
    articleName: string;
    isOnlyForAuto: boolean;
    packageType: number | null;
    packageItems: SaleArticleDetailPackageItemEditDto[];
    packageItemsError?: string;
}

export interface SaleArticleDetailPackageItemEditDto {
    id: number;
    inventoryArticleId?: number;
    saleArticleId?: number;
    quantity: number | null;
    quantityFormatted?: string;
    price: number | null;
    priceFormatted?: string;
}

function getAreItemsMatchingTemplate(ia: InventoryArticleDto, items: SaleArticleDetailPackageItemEditDto[], inventoryArticles: InventoryArticleDto[]): boolean {
    if (ia.isPackage && ia.showPackageContent && ia.showOnInvoice) {
        const ias = keyBy(inventoryArticles, p => p.id);
        return fullJoin(ia.packageItems, items, p => p.inventoryArticleId, p => p.inventoryArticleId)
            .every(p =>
                (p.hasBoth && p.a!.quantity === p.b!.quantity && ias[p.a!.inventoryArticleId!].showOnInvoice) ||
                (p.hasOnlyA && !ias[p.a!.inventoryArticleId].showOnInvoice));
    } else {
        return items.length === 0;
    }
}

function getPackageItemsFromTemplate(ia: InventoryArticleDto, packagePrice: number | null, allInventoryArticles: InventoryArticleDto[]): SaleArticleDetailPackageItemEditDto[] {
    if (!ia.isPackage || !ia.showPackageContent || !ia.showOnInvoice) {
        return [];
    }

    const packageItems = keyBy(ia.packageItems.map(p => ({ id: p.inventoryArticleId, quantity: p.quantity })), p => p.id);
    const inventoryArticles = allInventoryArticles.filter(p => packageItems[p.id] && p.showOnInvoice);
    const singleArticle = inventoryArticles.length === 1 ? inventoryArticles[0] : null;
    const singlePackageItem = singleArticle != null ? packageItems[singleArticle.id] : null;
    const price = singlePackageItem != null && packagePrice != null ? round(packagePrice / singlePackageItem.quantity, 2) : null;

    return inventoryArticles.map(p => ({
        id: newId(),
        inventoryArticleId: p.id,
        saleArticleId: p.saleArticles.length === 1 ? p.saleArticles[0].id : undefined,
        articleName: p.saleArticles.length === 1 ? `${p.saleArticles[0].code} ${p.saleArticles[0].name}` : `${p.code} ${p.name}`,
        price,
        quantity: packageItems[p.id].quantity,
    }));
}
